import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ButtonItem from '../components/ButtonItem';

const TYPE_STYLE = {
  CD: 'bg-sky-500/20 text-sky-300',
  LP: 'bg-violet-500/20 text-violet-300',
  굿즈: 'bg-amber-500/20 text-amber-300',
};

function PhysicalAlbumViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const page = urlParams.get('page') || 1;

  const user = useSelector((s) => s.auth?.me);
  const isAdmin = user?.adminYn === 'Y';

  const [item, setItem] = useState(null);

  useEffect(() => {
    axios.get(`/api/physical-albums/${id}`)
      .then((res) => setItem(res.data))
      .catch(() => navigate('/PhysicalAlbums'));
  }, [id, navigate]);

  const handleDelete = async () => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try {
      await axios.delete(`/api/physical-albums/${id}`);
      navigate(`/PhysicalAlbums?page=${page}`);
    } catch {
      alert('삭제 오류');
    }
  };

  const f = (n) => Number(n || 0).toLocaleString();

  if (!item) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mr-3" />
        <div className="text-sm text-slate-400">불러오는 중...</div>
      </div>
    );
  }

  const Field = ({ label, value, mono }) => (
    <div>
      <label className="block font-semibold mb-1">{label}</label>
      <input
        type="text"
        value={value ?? ''}
        readOnly
        className={`w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-300 ${mono ? 'font-mono' : ''}`}
      />
    </div>
  );

  return (
    <>
      <div className="flex mb-8 justify-center items-center">
        <h1 className="text-xl sm:text-2xl font-bold">음반 상세정보</h1>
      </div>

      <div className="space-y-6 max-w-2xl mx-auto">

        {/* 분배율 (권리사) */}
        {item.shares?.length > 0 && (
          <div>
            <label className="block mb-1">
              <span className="font-semibold">권리사명 </span>
              <span className="text-sm text-slate-400">(분배율)</span>
            </label>
            <div className="space-y-2">
              {item.shares.map((s, idx) => (
                <div key={idx} className="flex items-center gap-2 sm:gap-4">
                  <input
                    type="text"
                    value={s.client?.name || `권리사 ${s.clientId}`}
                    readOnly
                    className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-300"
                  />
                  <input
                    type="text"
                    value={`${s.rate}%`}
                    readOnly
                    className="w-20 sm:w-24 px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-right text-slate-300"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 커버 이미지 */}
        {item.coverUrl && (
          <div>
            <label className="block font-semibold mb-1">커버 이미지</label>
            <img
              src={item.coverUrl}
              alt={item.albumName}
              className="w-48 h-48 rounded-xl object-cover mx-auto mt-2 border border-white/[0.08]"
            />
          </div>
        )}

        {/* 음반명 / 음반코드 */}
        <div className="grid grid-cols-2 gap-4">
          <Field label="음반명" value={item.albumName} />
          <Field label="음반코드" value={item.code} mono />
        </div>

        {/* 아티스트 */}
        <Field label="아티스트명" value={item.artistName} />

        {/* 분류 */}
        <div>
          <label className="block font-semibold mb-1">분류</label>
          <div>
            <span className={`text-sm px-3 py-1 rounded-full ${TYPE_STYLE[item.type] || 'bg-slate-500/20 text-slate-300'}`}>
              {item.type}
            </span>
          </div>
        </div>

        {/* UPC / 바코드 */}
        <div className="grid grid-cols-2 gap-4">
          <Field label="UPC" value={item.upc} mono />
          <Field label="바코드" value={item.barcode} mono />
        </div>

        {/* 발매일 */}
        <Field label="발매일" value={item.releaseDate} />

        {/* 기획사 */}
        {item.label && <Field label="기획사" value={item.label} />}

        {/* 반품예치금 */}
        {item.returnDeposit != null && (
          <Field label="반품예치금 (원)" value={Number(item.returnDeposit).toLocaleString()} />
        )}

        {/* 출고가 / 수수료율 */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold mb-1">출고가 (원)</label>
            <input
              type="text"
              value={f(item.supplyPrice)}
              readOnly
              className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-300"
            />
          </div>
          <div>
            <label className="block font-semibold mb-1">유통수수료율 (%)</label>
            <input
              type="text"
              value={item.feeRate != null ? `${parseInt(item.feeRate)}%` : ''}
              readOnly
              className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-300"
            />
          </div>
        </div>

        {/* 재고 */}
        <div>
          <label className="block font-semibold mb-1">현재 재고</label>
          <input
            type="text"
            value={f(item.stock)}
            readOnly
            className={`w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] font-bold ${item.stock <= 0 ? 'text-rose-400' : 'text-emerald-400'}`}
          />
        </div>

        {/* 메모 */}
        {(item.memo || isAdmin) && (
          <div>
            <label className="block font-semibold mb-1">메모</label>
            <textarea
              rows={3}
              value={item.memo || ''}
              readOnly
              className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-300 resize-none"
            />
          </div>
        )}

      </div>

      <div className="flex justify-end gap-2 mt-10">
        <ButtonItem text="목록" color="gray" onClick={() => navigate(`/PhysicalAlbums?page=${page}`)} />
        {isAdmin && (
          <>
            <ButtonItem text="삭제" color="red" onClick={handleDelete} />
            <ButtonItem text="수정" color="blue" onClick={() => navigate(`/PhysicalAlbums/Modify/${id}?page=${page}`)} />
          </>
        )}
      </div>
    </>
  );
}

export default PhysicalAlbumViewPage;
